import type { ListRequest } from '@/api/generated/schemas';
import {
  defineEntityAsyncSelector,
  listRequestQueryKey,
  selectorPresets,
  toListRequest,
  type NoPickFilters,
} from '@/shared/ui/async-selector';
import { createAsyncSelectorField } from '@/shared/forms';

/** UI ref jednoduché legacy entity nabízené v selektoru (jen id + název). */
export interface LegacyEntityRef {
  id: number;
  name: string;
}

/** Minimální tvar suggestion DTO, který factory umí namapovat na {@link LegacyEntityRef}. */
interface LegacyEntitySuggestion {
  id: number;
  name?: string | null;
}

interface LegacyEntitySelectorConfig<TDto extends LegacyEntitySuggestion> {
  /** Generovaný suggestions fetch (`POST …/suggestions`). */
  fetch: (params: ListRequest) => Promise<{ items: readonly TDto[]; totalCount: number }>;
  /** Prefix react-query klíče (např. `'venua-planner.equipment'`). */
  queryKeyPrefix: string;
  /** Kategorie objektu pro list-picker (např. `'Equipment'`). */
  objectCategory: string;
  /** i18n klíč hlavičky jediného textového sloupce (název). */
  columnHeaderKey: string;
}

/**
 * Factory selektoru nad jednoduchou legacy entitou, která má v selektoru jen jeden sloupec (název).
 * Sjednocuje jinak identický wiring `Equipment`/`FacilitySubType` selektorů — liší se jen fetchem,
 * query-klíčem, kategorií a i18n hlavičkou (obdoba `createCodebookSelector`). Vrací typový selektor
 * (controlled) i form-bound field pro TanStack Form.
 */
export function createLegacyEntitySelector<TDto extends LegacyEntitySuggestion>(
  config: LegacyEntitySelectorConfig<TDto>
) {
  const selector = defineEntityAsyncSelector<LegacyEntityRef, TDto, ListRequest, NoPickFilters>({
    api: {
      fetch: config.fetch,
      buildApiParams: (ctx) => toListRequest(ctx.search, ctx.ids, ctx.extraFilters),
      queryKey: (request) => listRequestQueryKey(config.queryKeyPrefix, request),
      mapItem: (s) => ({ id: s.id, name: s.name ?? '' }),
    },
    key: (item) => item.id,
    objectCategory: config.objectCategory,
    display: { kind: 'firstColumn' },
    columns: (t) => [
      selectorPresets.textColumn({
        id: 'name',
        header: t(config.columnHeaderKey),
        value: (item) => item.name,
        sortable: true,
      }),
    ],
    i18n: { namespace: 'venua-planner' },
  });

  return {
    selector,
    Selector: selector.Selector,
    Field: createAsyncSelectorField(selector.Selector),
  };
}
